// PartyRail — left rail showing each PC with a portrait chip, HP bar, AC, conditions;
// opens the character sheet drawer. HP changes float a +/- number off the row and flash
// it; a downed PC shows death-save pips. AI seats get a "take turn" affordance.
import { useEffect, useRef, useState } from "react";
import type { PartyMember, Snapshot, WsClientMessage } from "../lib/types";
import { uid } from "../lib/format";
import { HpBar } from "./ui";
import { PixelArt } from "./PixelArt";

interface FloatNum { uid: string; id: string; amount: number; }

export function PartyRail({
  snapshot,
  aiCharacterIds,
  onOpenSheet,
  send,
  busy,
  gameId,
  art,
}: {
  snapshot: Snapshot | null;
  aiCharacterIds: Set<string>;
  onOpenSheet: (characterId: string) => void;
  send: (msg: WsClientMessage) => boolean;
  busy: boolean;
  // Optional — when supplied, each PC gets its cached portrait as a 28px thumb; absent,
  // an initial-letter chip stands in.
  gameId?: string;
  art?: Record<string, string>;
}) {
  const party = snapshot?.party ?? [];
  // The conductor's spotlight (out of combat only): mark the awaited seat with a glow +
  // ✨. In a fight the combat tracker owns turn order, so we don't double-mark.
  const awaiting = snapshot?.combat ? null : (snapshot?.awaiting ?? null);

  // Float a +/- number off a row whenever its HP changes (diff prev vs new snapshot),
  // and flash the row. Baseline captured on first sight so a fresh mount doesn't float.
  const prevHp = useRef<Record<string, number>>({});
  const [floats, setFloats] = useState<FloatNum[]>([]);
  const [flashing, setFlashing] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fresh: FloatNum[] = [];
    const flash = new Set<string>();
    for (const p of party) {
      const prev = prevHp.current[p.id];
      if (prev != null && prev !== p.hp) {
        fresh.push({ uid: uid("fl"), id: p.id, amount: p.hp - prev });
        flash.add(p.id);
      }
      prevHp.current[p.id] = p.hp;
    }
    if (fresh.length === 0) return;
    setFloats((f) => [...f, ...fresh]);
    setFlashing(flash);
    const t = window.setTimeout(() => {
      setFloats((f) => f.filter((x) => !fresh.some((n) => n.uid === x.uid)));
      setFlashing(new Set());
    }, 1100);
    return () => window.clearTimeout(t);
  }, [party]);

  return (
    <div className="play__left">
      <div className="rail-section">
        <div className="rail-head"><span>The Party</span><span>{party.length}</span></div>
        {party.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No one is seated.</div>}
        {party.map((p) => (
          <PartyCard key={p.id} p={p} isAi={aiCharacterIds.has(p.id)}
            floats={floats.filter((f) => f.id === p.id)}
            flash={flashing.has(p.id)} spotlighted={awaiting === p.id}
            gameId={gameId} artUrl={art?.[`character:${p.id}`]}
            onOpen={() => onOpenSheet(p.id)}
            onTurn={() => send({ action: "ai_player_turn", character_id: p.id })} busy={busy} />
        ))}
      </div>

      {snapshot && snapshot.party_gold > 0 && (
        <div className="rail-section">
          <div className="rail-head"><span>Party Coin</span></div>
          <span className="chip chip--gold">{snapshot.party_gold} gp</span>
        </div>
      )}
    </div>
  );
}

function PartyCard({
  p, isAi, floats, flash, spotlighted, gameId, artUrl, onOpen, onTurn, busy,
}: {
  p: PartyMember;
  isAi: boolean;
  floats: FloatNum[];
  flash: boolean;
  spotlighted: boolean;
  gameId?: string;
  artUrl?: string;
  onOpen: () => void;
  onTurn: () => void;
  busy: boolean;
}) {
  const down = p.hp <= 0;
  return (
    <div className={`pc-row${flash ? " hit-flash" : ""}${down ? " pc-row--down" : ""}${spotlighted ? " pc-row--spotlight" : ""}`}
      onClick={onOpen}
      style={{ position: "relative", ...(spotlighted ? { boxShadow: "inset 0 0 0 1.5px var(--gold)", borderRadius: "var(--radius)" } : null) }}>
      {/* Floating HP deltas (crimson for damage, jade heal). */}
      {floats.map((f) => (
        <span key={f.uid}
          className={`float-num${f.amount > 0 ? " float-num--heal" : ""}`}
          style={{ position: "absolute", right: 10, top: 6, pointerEvents: "none", fontWeight: 800 }}>
          {f.amount > 0 ? `+${f.amount}` : f.amount}
        </span>
      ))}
      <div className="pc-row__line" style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Avatar p={p} gameId={gameId} artUrl={artUrl} down={down} />
        <span className="pc-row__name">{p.name}</span>
        {spotlighted && <span title="It's their moment" aria-label="spotlighted" style={{ fontSize: "var(--fs-sm)" }}>✨</span>}
        {isAi && <span className="chip chip--ai">AI</span>}
        <span className="pc-row__spacer" />
        <span className="pc-row__ac" title="Armor Class">🛡 {p.ac}</span>
      </div>
      <div className="pc-row__hpwrap">
        <HpBar hp={p.hp} max={p.max_hp} temp={p.temp_hp} />
        <span className="pc-row__hp num">{p.hp}/{p.max_hp}{p.temp_hp > 0 ? `+${p.temp_hp}` : ""}</span>
      </div>
      {down && (
        <div className="death-pips" title="Making death saves"
          style={{ display: "flex", gap: 4, marginTop: 4 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} aria-hidden
              style={{ width: 8, height: 8, borderRadius: "50%", border: "1.5px solid var(--crimson-bright, #e5484d)",
                       animation: "hearthPulse 1.1s ease-in-out infinite" }} />
          ))}
          <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>death saves</span>
        </div>
      )}
      {(p.conditions.length > 0 || p.concentration) && (
        <div className="pc-row__meta">
          {p.concentration && <span className="chip chip--conc">◎ {p.concentration}</span>}
          {p.conditions.map((c) => <span key={c} className="chip chip--cond">{c}</span>)}
        </div>
      )}
      {isAi && (
        <button className="btn btn--sm pc-row__turn" disabled={busy}
          onClick={(e) => { e.stopPropagation(); onTurn(); }}>Take turn</button>
      )}
    </div>
  );
}

// 28px portrait chip: the cached character art when we have a gameId, else an
// initial-letter tile. Grayscale when the PC is down.
function Avatar({ p, gameId, artUrl, down }: { p: PartyMember; gameId?: string; artUrl?: string; down: boolean }) {
  const style = down ? { filter: "grayscale(1)", opacity: 0.7 } : undefined;
  if (gameId) {
    return (
      <div style={style}>
        <PixelArt shape="thumb" alt={p.name} liveUrl={artUrl}
          body={{ kind: "character", game_id: gameId, character_id: p.id }} />
      </div>
    );
  }
  const initial = p.name.trim().charAt(0).toUpperCase() || "?";
  return (
    <span className="portrait-thumb" aria-hidden
      style={{ width: 28, height: 28, flex: "0 0 28px", display: "flex", alignItems: "center",
               justifyContent: "center", borderRadius: 6, fontWeight: 700, fontSize: 13,
               background: "var(--panel-2, rgba(127,127,127,0.18))", ...style }}>
      {initial}
    </span>
  );
}
